import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { MembershipApi } from "../../api/MembershipApi";
import type { CreateMembershipType } from "../../generated/dto/CreateMembershipType";
import type { DeleteMembershipTypeStaff } from "../../generated/dto/DeleteMembershipTypeStaff";
import type { MembershipMember } from "../../generated/dto/MembershipMember";
import type { MembershipMemberFetchOptions } from "../../generated/dto/MembershipMemberFetchOptions";
import type { MembershipMemberSearchResult } from "../../generated/dto/MembershipMemberSearchResult";
import type { MembershipSearchParams } from "../../generated/dto/MembershipSearchParams";
import type { MembershipStaff } from "../../generated/dto/MembershipStaff";
import type { MembershipStaffFetchOptions } from "../../generated/dto/MembershipStaffFetchOptions";
import type { MembershipStaffSearchResult } from "../../generated/dto/MembershipStaffSearchResult";
import type { MembershipTypeFetchOptions } from "../../generated/dto/MembershipTypeFetchOptions";
import type { MembershipTypeMember } from "../../generated/dto/MembershipTypeMember";
import type { MembershipTypeSearchParams } from "../../generated/dto/MembershipTypeSearchParams";
import type { MembershipTypeStaff } from "../../generated/dto/MembershipTypeStaff";
import type { MembershipTypeStaffSearchResult } from "../../generated/dto/MembershipTypeStaffSearchResult";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonMember } from "../../generated/dto/PersonMember";
import type { PersonMemberFetchOptions } from "../../generated/dto/PersonMemberFetchOptions";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { ProgramTypeStaff } from "../../generated/dto/ProgramTypeStaff";
import type { UpdateMembershipType } from "../../generated/dto/UpdateMembershipType";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class MembershipApiClient implements MembershipApi {
  constructor(private readonly client = useClient()) {}

  async getMembershipTypeStaff(id: number, options?: MembershipTypeFetchOptions): Promise<MembershipTypeStaff> {
    const { data } = await this.client.get<unknown>(`/membershiptype/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.MembershipTypeFetchOptions") });
    return deserializeCbiDto<MembershipTypeStaff>(data, "generated.dto.MembershipTypeStaff");
  }

  async getMembershipMember(id: number, options?: MembershipMemberFetchOptions): Promise<MembershipMember> {
    const { data } = await this.client.get<unknown>(`/membership/member/${id}`, { params: serializeCbiDto(options, "generated.dto.MembershipMemberFetchOptions") });
    return deserializeCbiDto<MembershipMember>(data, "generated.dto.MembershipMember");
  }

  async getMembershipStaff(id: number, options?: MembershipStaffFetchOptions): Promise<MembershipStaff> {
    const { data } = await this.client.get<unknown>(`/membership/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.MembershipStaffFetchOptions") });
    return deserializeCbiDto<MembershipStaff>(data, "generated.dto.MembershipStaff");
  }

  async getAllMembershipTypeStaffs(options?: MembershipTypeFetchOptions): Promise<Array<MembershipTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/membershiptype/staff/all`, { params: serializeCbiDto(options, "generated.dto.MembershipTypeFetchOptions") });
    return deserializeCbiDto<Array<MembershipTypeStaff>>(data, "generated.dto.MembershipTypeStaff");
  }

  async createMembershipTypeStaff(source: CreateMembershipType): Promise<MembershipTypeStaff> {
    const { data } = await this.client.post<unknown>(`/membershiptype/staff`, serializeCbiDto(source, "generated.dto.CreateMembershipType"));
    return deserializeCbiDto<MembershipTypeStaff>(data, "generated.dto.MembershipTypeStaff");
  }

  async updateMembershipTypeStaff(source: UpdateMembershipType): Promise<MembershipTypeStaff> {
    const { data } = await this.client.put<unknown>(`/membershiptype/staff/${source.membershipTypeId}`, serializeCbiDto(source, "generated.dto.UpdateMembershipType"));
    return deserializeCbiDto<MembershipTypeStaff>(data, "generated.dto.MembershipTypeStaff");
  }

  async deleteMembershipTypeStaff(source: DeleteMembershipTypeStaff): Promise<void> {
    await this.client.delete(`/membershiptype/staff/${source.membershipTypeId}`);
  }

  async searchMembershipTypeStaff(params: MembershipTypeSearchParams, page: number, perPage: number): Promise<PaginationResult<MembershipTypeStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/membershiptype/staff/search`, serializeCbiDto(params, "generated.dto.MembershipTypeSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<MembershipTypeStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<MembershipTypeStaffSearchResult>>(result.items, "generated.dto.MembershipTypeStaffSearchResult") };
    return result;
  }

  async searchMembershipMember(params: MembershipSearchParams, page: number, perPage: number): Promise<PaginationResult<MembershipMemberSearchResult>> {
    const { data } = await this.client.post<unknown>(`/membership/member/search`, serializeCbiDto(params, "generated.dto.MembershipSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<MembershipMemberSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<MembershipMemberSearchResult>>(result.items, "generated.dto.MembershipMemberSearchResult") };
    return result;
  }

  async searchMembershipStaff(params: MembershipSearchParams, page: number, perPage: number): Promise<PaginationResult<MembershipStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/membership/staff/search`, serializeCbiDto(params, "generated.dto.MembershipSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<MembershipStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<MembershipStaffSearchResult>>(result.items, "generated.dto.MembershipStaffSearchResult") };
    return result;
  }

  async searchMembershipTypeStaffFull(params: MembershipTypeSearchParams, page: number, perPage: number, options?: MembershipTypeFetchOptions): Promise<PaginationResult<MembershipTypeStaff>> {
    const { data } = await this.client.post<unknown>(`/membershiptype/staff/search/full`, serializeCbiDto(params, "generated.dto.MembershipTypeSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.MembershipTypeFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<MembershipTypeStaff>;
    result = { ...result, items: deserializeCbiDto<Array<MembershipTypeStaff>>(result.items, "generated.dto.MembershipTypeStaff") };
    return result;
  }

  async searchMembershipMemberFull(params: MembershipSearchParams, page: number, perPage: number, options?: MembershipMemberFetchOptions): Promise<PaginationResult<MembershipMember>> {
    const { data } = await this.client.post<unknown>(`/membership/member/search/full`, serializeCbiDto(params, "generated.dto.MembershipSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.MembershipMemberFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<MembershipMember>;
    result = { ...result, items: deserializeCbiDto<Array<MembershipMember>>(result.items, "generated.dto.MembershipMember") };
    return result;
  }

  async searchMembershipStaffFull(params: MembershipSearchParams, page: number, perPage: number, options?: MembershipStaffFetchOptions): Promise<PaginationResult<MembershipStaff>> {
    const { data } = await this.client.post<unknown>(`/membership/staff/search/full`, serializeCbiDto(params, "generated.dto.MembershipSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.MembershipStaffFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<MembershipStaff>;
    result = { ...result, items: deserializeCbiDto<Array<MembershipStaff>>(result.items, "generated.dto.MembershipStaff") };
    return result;
  }

  async getMembershipTypeStaffProgramId(id: number): Promise<ProgramTypeStaff | null> {
    const { data } = await this.client.get<unknown>(`/membershiptype/staff/${id}/programid`);
    return deserializeCbiDto<ProgramTypeStaff | null>(data, "generated.dto.ProgramTypeStaff");
  }

  async getMembershipMemberPersonId(id: number, options?: PersonMemberFetchOptions): Promise<PersonMember> {
    const { data } = await this.client.get<unknown>(`/membership/member/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonMemberFetchOptions") });
    return deserializeCbiDto<PersonMember>(data, "generated.dto.PersonMember");
  }

  async getMembershipStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/membership/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getMembershipStaffMembershipTypeId(id: number, options?: MembershipTypeFetchOptions): Promise<MembershipTypeStaff> {
    const { data } = await this.client.get<unknown>(`/membership/staff/${id}/membershiptypeid`, { params: serializeCbiDto(options, "generated.dto.MembershipTypeFetchOptions") });
    return deserializeCbiDto<MembershipTypeStaff>(data, "generated.dto.MembershipTypeStaff");
  }

  async purchaseOptionsMember(): Promise<Array<MembershipTypeMember>> {
    const { data } = await this.client.post<unknown>(`/membership/member/purchaseoptions`);
    return deserializeCbiDto<Array<MembershipTypeMember>>(data, "generated.dto.MembershipTypeMember");
  }

  async purchaseOptionsPublic(): Promise<Array<MembershipTypeMember>> {
    const { data } = await this.client.post<unknown>(`/membership/public/purchaseoptions`);
    return deserializeCbiDto<Array<MembershipTypeMember>>(data, "generated.dto.MembershipTypeMember");
  }
}

export const useMembershipApi = (): MembershipApi => new MembershipApiClient();
